import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// Flag to prevent localStorage writes before hydration completes.
// Zustand persist hydrates asynchronously even for synchronous localStorage.
// Without this guard, any setState() before hydration triggers the persist
// subscriber to write default null state, overwriting stored auth data.
let _hydrated = false;

interface User {
  id: string;
  email: string;
  fullName: string;
  isSuperAdmin: boolean;
  isActive: boolean;
  emailVerifiedAt: string | null;
  lastLoginAt: string | null;
}

interface TenantContext {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  roleId: string;
  roleName: string;
  permissions: string[];
}

interface AuthState {
  // State
  user: User | null;
  tenant: TenantContext | null;
  permissions: string[];
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  notProvisioned: boolean;

  // Actions
  setUser: (user: User | null) => void;
  setTenant: (tenant: TenantContext | null) => void;
  setSession: (user: User, tenant: TenantContext | null, permissions?: string[]) => void;
  setNotProvisioned: (notProvisioned: boolean) => void;
  setLoading: (isLoading: boolean) => void;
  isSuperAdmin: () => boolean;
  isTenantAdmin: () => boolean;
  hasPermission: (permission: string) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      tenant: null,
      permissions: [],
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,
      notProvisioned: false,

      // Computed as functions (not getters) to avoid crashes during hydration merge.
      // Zustand persist's merge step evaluates getters before the store is ready,
      // causing "Cannot read properties of undefined" errors.
      isSuperAdmin: () => get().user?.isSuperAdmin ?? false,
      isTenantAdmin: () => {
        const tenant = get().tenant;
        return tenant?.permissions?.includes('tenant.manage') ?? false;
      },

      // Actions
      setUser: (user) => {
        if (user?.email) {
          (window as any).__orchestra_user_email = user.email;
        }
        set({
          user,
          isAuthenticated: user !== null,
          isLoading: false,
          notProvisioned: false,
        });
      },

      setTenant: (tenant) => set({
        tenant,
        permissions: tenant?.permissions ?? [],
      }),

      setSession: (user, tenant, permissions) => {
        if (user.email) {
          (window as any).__orchestra_user_email = user.email;
        }
        set({
          user,
          tenant,
          permissions: permissions ?? tenant?.permissions ?? [],
          isAuthenticated: true,
          isLoading: false,
          notProvisioned: false,
        });
      },

      setNotProvisioned: (notProvisioned) => set({
        notProvisioned,
        isAuthenticated: false,
        isLoading: false,
      }),

      setLoading: (isLoading) => set({ isLoading }),

      hasPermission: (permission) => {
        const { tenant, user } = get();
        if (user?.isSuperAdmin) return true;
        if (!tenant?.permissions) return false;
        if (tenant.permissions.includes('*')) return true;
        return tenant.permissions.includes(permission);
      },
    }),
    {
      name: 'bizops-auth',
      storage: createJSONStorage(() => ({
        getItem: (name: string) => localStorage.getItem(name),
        setItem: (name: string, value: string) => {
          if (!_hydrated) return;
          localStorage.setItem(name, value);
        },
        removeItem: (name: string) => localStorage.removeItem(name),
      })),
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        tenant: state.tenant,
        permissions: state.permissions,
      }),
      // onRehydrateStorage returns a callback that fires after hydration.
      // We merge isAuthenticated and isLoading directly into the hydrated state
      // via the `merge` option below, avoiding the need to call useAuthStore.setState()
      // which fails with "Cannot access before initialization" during store creation.
      onRehydrateStorage: () => (state) => {
        _hydrated = true;
        if (state?.user?.email) {
          (window as any).__orchestra_user_email = state.user.email;
        }
      },
      merge: (persistedState, currentState) => {
        const persisted = (persistedState as Partial<AuthState>) || {};
        return {
          ...currentState,
          ...persisted,
          permissions: persisted.tenant?.permissions ?? persisted.permissions ?? [],
          // Access identity is verified by /auth/me during bootstrap.
          isAuthenticated: persisted.user != null,
          notProvisioned: false,
          // Hydration is complete, stop loading
          isLoading: false,
        };
      },
    }
  )
);
